import React, { useLayoutEffect, useState } from 'react';
import styled, { css } from 'styled-components';
import theme from 'styled-theming';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Controller } from 'swiper/modules';
import type { Swiper as SwiperType } from 'swiper';
import 'swiper/css';

/**
 * StyledSelectionList - Standalone styled-components selection list
 *
 * This is a complete standalone component using styled-components,
 * following the PClash design system pattern.
 */

// Button styles
const StyledButton = styled.button<{ $active?: boolean; $type?: 'list' | 'group' }>`
  height: 40px;
  border-radius: 8px;
  padding: 0 14px;
  /* No transition on color/background/border/shadow — the active pill
   * swaps instantly. A 300ms fade between the previously active pill and
   * the newly active one (e.g. on scroll-driven active-round updates,
   * which can fire many times per second) read as "blinking" because
   * consecutive fades overlap mid-transition. */
  line-height: 1;
  border: 1px solid var(--border);
  font-size: 0.875rem;
  font-weight: 600;
  font-family: var(--heading-font);
  cursor: pointer;
  white-space: nowrap;
  touch-action: manipulation;
  /* Suppress iOS native tap-highlight overlay on pills.
   * Override of the project default ("keep tap-flash as feedback") — here
   * the React-driven $active style is the canonical feedback signal, and
   * iOS WKWebView (Telegram Mini App) was rendering a transient grey overlay
   * on tap that the user read as "blinking" because it competed with the
   * pill's active-state colour swap. Pills are the one place where the
   * native flash actively conflicts with our visual feedback. */
  -webkit-tap-highlight-color: transparent;
  /* Block accidental text selection on tap-and-hold — pill labels are not
   * meant to be selectable. */
  user-select: none;

  /* Compact pill on mobile / phone-sized viewports (< 768px). Tablet+ keeps the larger pill. */
  @media (max-width: 767px) {
    height: 32px;
    border-radius: 6px;
    padding: 0 10px;
    font-size: 0.8125rem;
  }

  /* List type (default) */
  ${props => (!props.$type || props.$type === 'list') && css`
    color: ${theme('theme', {
      light: 'var(--highlight)',
      dark: 'var(--text)'
    })};
    background-color: ${theme('theme', {
      light: 'var(--widget)',
      dark: 'var(--border)'
    })};

    /* No :hover / :focus / :active highlight on the pill itself.
     * Only the React-driven $active prop drives the highlighted look.
     * Reason: simultaneous mousedown :active flash + the 300ms background
     * transition between the previously active pill and the newly clicked
     * one produced a perceived "blink" (old pill fades out while clicked
     * pill flashes in via :active before React state catches up). Treating
     * the active state as the single source of truth eliminates the dual
     * highlight. */

    ${props.$active && css`
      ${theme('theme', {
        light: `
          filter: drop-shadow(0px 1px 8px rgba(110, 110, 110, 0.1));
          border-color: var(--widget);
          background-color: var(--widget);
          color: var(--highlight);
        `,
        dark: `
          background-color: var(--widget);
          border-color: var(--widget);
          box-shadow: 0 1px 8px rgba(33, 33, 33, 0.1);
          color: var(--accent);
        `
      })}
    `}
  `}

  /* Group type — same single-source-of-truth rule as List type above. */
  ${props => props.$type === 'group' && css`
    color: var(--accent);
    background-color: var(--widget);

    ${props.$active && css`
      background-color: var(--border);
    `}
  `}
`;

// Navigation button gradient mixins
const prevButtonGradient = css`
  padding-left: 10px;
  justify-content: flex-start;
  background: ${theme('theme', {
    light: 'linear-gradient(90deg, #FFFFFF 28.49%, rgba(255, 255, 255, 0.0001) 100%)',
    dark: 'linear-gradient(-90deg, rgba(17, 19, 18, 0.0001) 0%, #111312 77.09%)'
  })};
  border-radius: 4px 0 0 0;
`;

const nextButtonGradient = css`
  padding-right: 10px;
  justify-content: flex-end;
  background: ${theme('theme', {
    light: 'linear-gradient(-90deg, #FFFFFF 28.49%, rgba(255, 255, 255, 0.0001) 100%)',
    dark: 'linear-gradient(90deg, rgba(17, 19, 18, 0.0001) 0%, #111312 77.09%)'
  })};
  border-radius: 0 4px 0 0;
`;

// Main container
const StyledContainer = styled.div`
  display: flex;
  gap: 20px;
  box-shadow: 0 1px 8px rgba(110, 110, 110, 0.1);
  margin-bottom: 10px;
  background: ${theme('theme', {
    light: 'var(--widget)',
    dark: '#1B1F23'
  })};
  position: relative;
  overflow: hidden;

  @media (max-width: 767px) {
    margin-bottom: 6px;
  }

  /* Navigation overlay — hidden on touch devices (no hover); touch users swipe instead. */
  .nav {
    display: flex;
    align-items: center;
    justify-content: space-between;
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 10;
    pointer-events: none;

    &.ltr {
      flex-direction: row;
    }

    &.rtl {
      flex-direction: row-reverse;
    }

    @media (hover: none) {
      display: none;
    }
  }

  /* Navigation buttons */
  .nav-btn {
    width: 50px;
    height: 100%;
    display: flex;
    align-items: center;
    color: #8EA2AB;
    background: transparent;
    border: none;
    cursor: pointer;
    padding: 0;
    pointer-events: auto;
    transition: color var(--transition);
    touch-action: manipulation;

    @media (hover: hover) {
      &:hover {
        color: var(--highlight);
      }
    }
  }

  .nav-btn-prev {
    ${prevButtonGradient}
  }

  .nav-btn-next {
    ${nextButtonGradient}
  }

  /* Chevron icons */
  .icon-chevron {
    display: block;
    width: 24px;
    height: 24px;
    position: relative;

    &::before {
      content: '';
      position: absolute;
      top: 50%;
      left: 50%;
      width: 10px;
      height: 10px;
      border-left: 2px solid currentColor;
      border-bottom: 2px solid currentColor;
    }
  }

  .icon-chevron-left::before {
    transform: translate(-30%, -50%) rotate(45deg);
  }

  .icon-chevron-right::before {
    transform: translate(-70%, -50%) rotate(-135deg);
  }

  /* Swiper container */
  .swiper {
    width: 100%;
    height: 100%;
    padding: 20px 0;

    @media (max-width: 767px) {
      padding: 10px 0;
    }
  }

  .swiper-slide {
    width: fit-content !important;
  }
`;

export interface SelectionOption {
  label: string;
  value: string | number;
}

export interface StyledSelectionListProps {
  active: string | number;
  setActive: (value: string | number) => void;
  options: SelectionOption[];
  innerRef?: React.RefObject<HTMLDivElement>;
  /** Button type: 'list' (default) or 'group' */
  type?: 'list' | 'group';
  /** Swiper instance to control (for two-way sync with content swiper) */
  controlledSwiper?: SwiperType | null;
  /** Callback to expose this component's swiper instance */
  onSwiper?: (swiper: SwiperType) => void;
}

export const StyledSelectionList: React.FC<StyledSelectionListProps> = ({
  active,
  setActive,
  options,
  innerRef,
  type = 'list',
  controlledSwiper,
  onSwiper
}) => {
  const [swiper, setSwiper] = useState<SwiperType | null>(null);
  const direction = 'ltr'; // Can be made dynamic if RTL support needed

  // Resolve initial slide synchronously so Swiper mounts pre-positioned on the
  // active pill. Without this, Swiper mounts on slide 0 for one paint frame
  // before the sync effect runs, producing a visible "ugly scroll" animation
  // on cold reload. MatchesTab guarantees `active` is a valid value at mount
  // time (renders only after detectInitialMatchday resolves), so this index
  // is always correct on first paint.
  const activeIndex = options.findIndex(opt => opt.value === active);

  // Expose swiper instance to parent
  const handleSwiper = (swiperInstance: SwiperType) => {
    setSwiper(swiperInstance);
    onSwiper?.(swiperInstance);
  };

  const handlePrev = () => {
    if (swiper) {
      swiper.slidePrev();
      const option = options[swiper.realIndex];
      if (option) {
        setActive(option.value);
      }
    }
  };

  const handleNext = () => {
    if (swiper) {
      swiper.slideNext();
      const option = options[swiper.realIndex];
      if (option) {
        setActive(option.value);
      }
    }
  };

  // Sync the Swiper's slide position with the React-driven `active` prop.
  // The pill swiper has centeredSlides=true, so calling slideTo(activeIndex)
  // re-centres the active pill in the viewport without animation. This is
  // the only mechanism that drives pill-rail centering — consumers no longer
  // bind a `controlledSwiper` to a content Swiper via the `Controller`
  // module; `MatchesTab` renders a vertically scrolled continuous list and
  // updates `active` from a scroll listener instead. The `Controller` import
  // and `controlledSwiper` prop are retained for the rare consumer that
  // still pairs this rail with a paged content swiper.
  // Triggers: pill tap (button.onClick → setActive), scroll-driven active-
  // round detection in the consumer (e.g. MatchesTab's scroll listener),
  // and initial round detection on mount.
  //
  // MUST be useLayoutEffect, NOT useEffect. The highlight swap (driven by
  // the `$active` prop on StyledButton) is committed in the same React pass
  // as `active`/`activeIndex` change, but the wrapper transform that re-
  // centres the new active pill is imperative on the Swiper instance. With
  // useEffect, the transform update is deferred until AFTER the browser
  // paints the post-commit DOM — so the user sees one frame where the new
  // active pill is highlighted at its old off-centre position and the
  // centre slot is the now-unhighlighted previously-active pill ("blink"),
  // then a second frame where the row slides into place. useLayoutEffect
  // runs synchronously after commit but BEFORE paint, so the highlight
  // swap and the slideTo transform land in the same paint — one frame,
  // no blink. The duration-0 slideTo is instant so there's no animation
  // that could tear across paints.
  useLayoutEffect(() => {
    if (swiper && active !== undefined) {
      if (activeIndex !== -1 && swiper.realIndex !== activeIndex) {
        // duration 0 = instant (matches the no-transition pill UX);
        // runCallbacks=false avoids firing onSlideChange, which could
        // re-enter setActive in a feedback loop if a consumer ever wires it.
        swiper.slideTo(activeIndex, 0, false);
      }
    }
  }, [swiper, active, activeIndex]);

  return (
    <StyledContainer ref={innerRef}>
      <div className={`nav ${direction}`}>
        <button
          className="nav-btn nav-btn-prev"
          aria-label="Previous"
          onClick={handlePrev}
          type="button"
        >
          <i className="icon-chevron icon-chevron-left" />
        </button>
        <button
          className="nav-btn nav-btn-next"
          aria-label="Next"
          onClick={handleNext}
          type="button"
        >
          <i className="icon-chevron icon-chevron-right" />
        </button>
      </div>
      <Swiper
        className="swiper"
        modules={[Controller]}
        onSwiper={handleSwiper}
        controller={controlledSwiper ? { control: controlledSwiper } : undefined}
        slidesPerView="auto"
        spaceBetween={12}
        loop={false}
        centeredSlides={true}
        initialSlide={activeIndex >= 0 ? activeIndex : 0}
      >
        {options.map(option => (
          <SwiperSlide key={option.value} className="swiper-slide">
            <StyledButton
              $active={active === option.value}
              $type={type}
              onClick={() => setActive(option.value)}
              type="button"
            >
              {option.label}
            </StyledButton>
          </SwiperSlide>
        ))}
      </Swiper>
    </StyledContainer>
  );
};

export default StyledSelectionList;
